/**
 * Pixel-based hit detection utilities for canvas elements
 */

/**
 * Check if a point is within a circle
 * @param {number} x - Point x coordinate
 * @param {number} y - Point y coordinate
 * @param {number} centerX - Circle center x
 * @param {number} centerY - Circle center y
 * @param {number} radius - Circle radius
 * @returns {boolean} - True if point is inside circle
 */
export const isPointInCircle = (x, y, centerX, centerY, radius) => {
  const dx = x - centerX;
  const dy = y - centerY;
  return dx * dx + dy * dy <= radius * radius;
};

/**
 * Check if a point is within an ellipse
 * @param {number} x - Point x coordinate
 * @param {number} y - Point y coordinate
 * @param {number} centerX - Ellipse center x
 * @param {number} centerY - Ellipse center y
 * @param {number} radiusX - Ellipse horizontal radius
 * @param {number} radiusY - Ellipse vertical radius
 * @returns {boolean} - True if point is inside ellipse
 */
export const isPointInEllipse = (x, y, centerX, centerY, radiusX, radiusY) => {
  const dx = x - centerX;
  const dy = y - centerY;
  // Standard ellipse equation: (dx/radiusX)^2 + (dy/radiusY)^2 <= 1
  return (dx * dx) / (radiusX * radiusX) + (dy * dy) / (radiusY * radiusY) <= 1;
};

/**
 * Check if a point is within a rectangle
 * @param {number} x - Point x coordinate
 * @param {number} y - Point y coordinate
 * @param {number} rectX - Rectangle x position
 * @param {number} rectY - Rectangle y position
 * @param {number} width - Rectangle width
 * @param {number} height - Rectangle height
 * @returns {boolean} - True if point is inside rectangle
 */
export const isPointInRectangle = (x, y, rectX, rectY, width, height) => {
  return x >= rectX && x <= rectX + width && y >= rectY && y <= rectY + height;
};

/**
 * Check if a point is within a polygon
 * @param {number} x - Point x coordinate
 * @param {number} y - Point y coordinate
 * @param {Array} points - Array of polygon points [x1, y1, x2, y2, ...]
 * @returns {boolean} - True if point is inside polygon
 */
export const isPointInPolygon = (x, y, points) => {
  let inside = false;
  for (let i = 0, j = points.length - 2; i < points.length; j = i, i += 2) {
    const xi = points[i];
    const yi = points[i + 1];
    const xj = points[j];
    const yj = points[j + 1];

    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) {
      inside = !inside;
    }
  }
  return inside;
};

/**
 * Check if a point is within a line (with tolerance)
 * @param {number} x - Point x coordinate
 * @param {number} y - Point y coordinate
 * @param {number} x1 - Line start x
 * @param {number} y1 - Line start y
 * @param {number} x2 - Line end x
 * @param {number} y2 - Line end y
 * @param {number} tolerance - Hit detection tolerance
 * @returns {boolean} - True if point is near line
 */
export const isPointNearLine = (x, y, x1, y1, x2, y2, tolerance = 5) => {
  const A = x - x1;
  const B = y - y1;
  const C = x2 - x1;
  const D = y2 - y1;

  const dot = A * C + B * D;
  const lenSq = C * C + D * D;

  if (lenSq === 0) {
    // Line is a point
    return Math.sqrt(A * A + B * B) <= tolerance;
  }

  const param = dot / lenSq;

  let xx, yy;
  if (param < 0) {
    xx = x1;
    yy = y1;
  } else if (param > 1) {
    xx = x2;
    yy = y2;
  } else {
    xx = x1 + param * C;
    yy = y1 + param * D;
  }

  const dx = x - xx;
  const dy = y - yy;
  return Math.sqrt(dx * dx + dy * dy) <= tolerance;
};

/**
 * Get distance between two points
 * @param {number} x1 - First point x
 * @param {number} y1 - First point y
 * @param {number} x2 - Second point x
 * @param {number} y2 - Second point y
 * @returns {number} - Distance between points
 */
export const getDistance = (x1, y1, x2, y2) => {
  const dx = x2 - x1;
  const dy = y2 - y1;
  return Math.sqrt(dx * dx + dy * dy);
};

/**
 * Check if a point is within a text element (approximate)
 * @param {number} x - Point x coordinate
 * @param {number} y - Point y coordinate
 * @param {number} textX - Text x position
 * @param {number} textY - Text y position
 * @param {number} width - Text width
 * @param {number} height - Text height
 * @returns {boolean} - True if point is within text bounds
 */
export const isPointInText = (x, y, textX, textY, width, height) => {
  return isPointInRectangle(x, y, textX, textY, width, height);
};

/**
 * Minimum hit stroke width for hit detection (ensures clickable area is always sufficient)
 * This is independent of the visual stroke width - even if stroke is 1px, hit detection uses this minimum
 */
const MIN_HIT_STROKE_WIDTH = 15; // Minimum hit detection stroke width in pixels

/**
 * Check if a point is on the stroke/border of a shape
 */
const isPointOnStroke = (clickPoint, shape) => {
  // Calculate effective stroke width accounting for zoom level
  const getEffectiveStrokeWidth = () => {
    const effectiveZoomLevel = shape.effectiveZoomLevel || 1;
    const visualStrokeWidth =
      (shape.strokeWidth || 2) / Math.max(0.3, effectiveZoomLevel || 1);
    // Always use at least MIN_HIT_STROKE_WIDTH for hit detection, regardless of visual stroke width
    // This ensures shapes with very thin strokes (1-2px) are still easy to click
    return Math.max(MIN_HIT_STROKE_WIDTH, visualStrokeWidth);
  };

  const strokeWidth = getEffectiveStrokeWidth();
  const halfStroke = strokeWidth / 2;
  // Use a minimum tolerance - but strokeWidth already ensures minimum, so this is just for safety
  const effectiveHalfStroke = Math.max(MIN_HIT_STROKE_WIDTH / 2, halfStroke);

  if (shape.type === "Rect" || shape.type === "Rectangle") {
    // Check if point is within stroke width of rectangle border
    const { x, y, width, height } = shape;

    // Calculate rectangle bounds
    const left = x;
    const right = x + width;
    const top = y;
    const bottom = y + height;

    // Calculate distance from point to each edge
    const distToLeft = Math.abs(clickPoint.x - left);
    const distToRight = Math.abs(clickPoint.x - right);
    const distToTop = Math.abs(clickPoint.y - top);
    const distToBottom = Math.abs(clickPoint.y - bottom);

    // Check if point is on left edge (within tolerance horizontally AND within extended vertical range)
    const onLeftEdge =
      distToLeft <= effectiveHalfStroke &&
      clickPoint.y >= top - effectiveHalfStroke &&
      clickPoint.y <= bottom + effectiveHalfStroke;

    // Check if point is on right edge (within tolerance horizontally AND within extended vertical range)
    const onRightEdge =
      distToRight <= effectiveHalfStroke &&
      clickPoint.y >= top - effectiveHalfStroke &&
      clickPoint.y <= bottom + effectiveHalfStroke;

    // Check if point is on top edge (within tolerance vertically AND within extended horizontal range)
    const onTopEdge =
      distToTop <= effectiveHalfStroke &&
      clickPoint.x >= left - effectiveHalfStroke &&
      clickPoint.x <= right + effectiveHalfStroke;

    // Check if point is on bottom edge (within tolerance vertically AND within extended horizontal range)
    const onBottomEdge =
      distToBottom <= effectiveHalfStroke &&
      clickPoint.x >= left - effectiveHalfStroke &&
      clickPoint.x <= right + effectiveHalfStroke;

    // For corners, calculate the minimum distance to any corner
    // Use a more generous tolerance for corners to handle diagonal clicks
    const cornerTolerance = strokeWidth; // Use full stroke width for corners

    const distToTopLeft = Math.sqrt(
      Math.pow(clickPoint.x - left, 2) + Math.pow(clickPoint.y - top, 2)
    );
    const distToTopRight = Math.sqrt(
      Math.pow(clickPoint.x - right, 2) + Math.pow(clickPoint.y - top, 2)
    );
    const distToBottomLeft = Math.sqrt(
      Math.pow(clickPoint.x - left, 2) + Math.pow(clickPoint.y - bottom, 2)
    );
    const distToBottomRight = Math.sqrt(
      Math.pow(clickPoint.x - right, 2) + Math.pow(clickPoint.y - bottom, 2)
    );

    // Point is on a corner if it's within corner tolerance distance
    const onCorner =
      distToTopLeft <= cornerTolerance ||
      distToTopRight <= cornerTolerance ||
      distToBottomLeft <= cornerTolerance ||
      distToBottomRight <= cornerTolerance;

    // Also check if point is near any edge segment (even outside the rectangle bounds)
    // This handles cases where click is just outside the rectangle but still on the stroke
    const nearLeftEdgeSegment = distToLeft <= effectiveHalfStroke;
    const nearRightEdgeSegment = distToRight <= effectiveHalfStroke;
    const nearTopEdgeSegment = distToTop <= effectiveHalfStroke;
    const nearBottomEdgeSegment = distToBottom <= effectiveHalfStroke;

    // Point is near an edge segment if it's close to an edge AND either:
    // 1. Within the extended bounds of that edge, OR
    // 2. On a corner (handled separately above)
    const nearEdgeSegment =
      (nearLeftEdgeSegment &&
        clickPoint.y >= top - strokeWidth &&
        clickPoint.y <= bottom + strokeWidth) ||
      (nearRightEdgeSegment &&
        clickPoint.y >= top - strokeWidth &&
        clickPoint.y <= bottom + strokeWidth) ||
      (nearTopEdgeSegment &&
        clickPoint.x >= left - strokeWidth &&
        clickPoint.x <= right + strokeWidth) ||
      (nearBottomEdgeSegment &&
        clickPoint.x >= left - strokeWidth &&
        clickPoint.x <= right + strokeWidth);

    // Point is on stroke if it's on any edge, corner, or near an edge segment
    const isOnStroke =
      onLeftEdge ||
      onRightEdge ||
      onTopEdge ||
      onBottomEdge ||
      onCorner ||
      nearEdgeSegment;


    return isOnStroke;
  }

  if (shape.type === "Circle") {
    // Check if point is within stroke width of circle/ellipse border
    const dx = clickPoint.x - shape.x;
    const dy = clickPoint.y - shape.y;

    // Determine if this is an ellipse (has different radiusX and radiusY) or a circle
    const radiusX = shape.radiusX || shape.radius || 50;
    const radiusY = shape.radiusY || shape.radius || 50;
    const isEllipse = radiusX !== radiusY;

    if (isEllipse) {
      // For ellipse, calculate the distance from the ellipse border
      // Normalize the point to ellipse space
      const normalizedDistance = Math.sqrt(
        (dx * dx) / (radiusX * radiusX) + (dy * dy) / (radiusY * radiusY)
      );

      // Calculate the actual distance from the ellipse border
      // normalizedDistance = 1 means exactly on the border
      const distanceFromBorder =
        Math.abs(normalizedDistance - 1) * Math.min(radiusX, radiusY);

      // Use minimum hit stroke width for consistency (strokeWidth already ensures MIN_HIT_STROKE_WIDTH)
      const effectiveHalfStroke = Math.max(
        MIN_HIT_STROKE_WIDTH / 2,
        halfStroke
      );
      const isOnStroke = distanceFromBorder <= effectiveHalfStroke;


      return isOnStroke;
    } else {
      // Circle logic
      const distance = Math.sqrt(dx * dx + dy * dy);
      const radius = shape.radius || shape.radiusX || 50;

      // Check if point is within stroke width of the circle border
      // Use minimum hit stroke width for consistency (strokeWidth already ensures MIN_HIT_STROKE_WIDTH)
      const effectiveHalfStroke = Math.max(
        MIN_HIT_STROKE_WIDTH / 2,
        halfStroke
      );
      const distanceFromCircumference = Math.abs(distance - radius);
      const isOnStroke = distanceFromCircumference <= effectiveHalfStroke;


      return isOnStroke;
    }
  }

  return false;
};

/**
 * Check if a point is in the fill area of a shape (excluding stroke)
 */
const isPointInFillArea = (clickPoint, shape) => {
  // Calculate effective stroke width accounting for zoom level
  // Note: For fill area detection, we use the visual stroke width (not the hit stroke width)
  // because we want to detect clicks in the actual fill area, not the extended hit area
  const getEffectiveStrokeWidth = () => {
    const effectiveZoomLevel = shape.effectiveZoomLevel || 1;
    return Math.max(
      1,
      (shape.strokeWidth || 2) / Math.max(0.3, effectiveZoomLevel || 1)
    );
  };

  const strokeWidth = getEffectiveStrokeWidth();
  const halfStroke = strokeWidth / 2;

  if (shape.type === "Rect" || shape.type === "Rectangle") {
    const { x, y, width, height } = shape;

    // For rectangles, we need to be more generous with the fill area detection
    // to match the behavior of circles. Instead of excluding the stroke area,
    // we'll consider the entire rectangle as a potential fill area.
    // The stroke detection will handle the actual stroke area separately.
    const inBounds =
      clickPoint.x >= x &&
      clickPoint.x <= x + width &&
      clickPoint.y >= y &&
      clickPoint.y <= y + height;

    // Debug logging for rectangle fill area detection
    if (process.env.NODE_ENV === "development") {
    }

    return inBounds;
  }

  if (shape.type === "Circle") {
    const dx = clickPoint.x - shape.x;
    const dy = clickPoint.y - shape.y;

    // Determine if this is an ellipse (has different radiusX and radiusY) or a circle
    const radiusX = shape.radiusX || shape.radius || 50;
    const radiusY = shape.radiusY || shape.radius || 50;
    const isEllipse = radiusX !== radiusY;

    if (isEllipse) {
      // For ellipse, calculate fill area accounting for stroke width
      // The fill area is the ellipse minus the stroke width
      const fillRadiusX = Math.max(0, radiusX - halfStroke);
      const fillRadiusY = Math.max(0, radiusY - halfStroke);

      // Check if point is within the fill ellipse using the ellipse equation
      const isInFillArea =
        (dx * dx) / (fillRadiusX * fillRadiusX) +
          (dy * dy) / (fillRadiusY * fillRadiusY) <=
        1;

      // Debug logging (can be removed later)
      if (process.env.NODE_ENV === "development") {
      }

      return isInFillArea;
    } else {
      // Circle logic
      const distance = Math.sqrt(dx * dx + dy * dy);
      const radius = shape.radius || shape.radiusX || 50;
      const fillRadius = Math.max(0, radius - halfStroke);

      const isInFillArea = distance <= fillRadius;

      // Debug logging (can be removed later)
      if (process.env.NODE_ENV === "development") {
      }

      return isInFillArea;
    }
  }

  return false;
};

/**
 * Get the fill opacity of a shape (considering transparency)
 */
const getShapeOpacity = (shape) => {
  // Check for various opacity properties in order of priority
  if (shape.fillOpacity !== undefined && shape.fillOpacity !== null) {
    return shape.fillOpacity;
  }

  if (shape.opacity !== undefined && shape.opacity !== null) {
    return shape.opacity;
  }

  // Parse opacity from fill color if it's rgba/hsla
  const fillColor = shape.fillColor || shape.fill;
  if (typeof fillColor === "string") {
    const rgbaMatch = fillColor.match(/rgba?\([^)]+\)/);
    if (rgbaMatch) {
      const values = rgbaMatch[0].match(/\d+(\.\d+)?/g);
      if (values && values.length >= 4) {
        const alpha = parseFloat(values[3]);
        return alpha;
      }
    }

    const hslaMatch = fillColor.match(/hsla?\([^)]+\)/);
    if (hslaMatch) {
      const values = hslaMatch[0].match(/\d+(\.\d+)?/g);
      if (values && values.length >= 4) {
        const alpha = parseFloat(values[3]);
        return alpha;
      }
    }
  }

  // Default to fully opaque
  return 1;
};

/**
 * Check if a point is within an image bounds
 */
const isPointInImage = (point, image) => {
  // Images are positioned by their center with offsetX/offsetY, so we need to calculate bounds
  const halfWidth = image.width / 2;
  const halfHeight = image.height / 2;

  const left = image.x - halfWidth;
  const right = image.x + halfWidth;
  const top = image.y - halfHeight;
  const bottom = image.y + halfHeight;

  return (
    point.x >= left && point.x <= right && point.y >= top && point.y <= bottom
  );
};

/**
 * Advanced hit detection that distinguishes between stroke and fill areas
 */
export const checkAdvancedHitDetection = (clickPoint, shape, images) => {

  // PRIORITY 0: Check stroke first (stroke extends beyond shape bounds, so check this before bounds check)
  const isOnStroke = isPointOnStroke(clickPoint, shape);


  // If clicking on stroke, always select the shape regardless of transparency or underlying images
  if (isOnStroke) {
    return {
      shouldSelectShape: true,
      shouldSelectImage: false,
      isOnStroke: true,
    };
  }

  // Now check if the click is within the shape bounds (for fill area detection)
  let isInShape = false;

  if (shape.type === "Rect" || shape.type === "Rectangle") {
    isInShape = isPointInRectangle(
      clickPoint.x,
      clickPoint.y,
      shape.x,
      shape.y,
      shape.width,
      shape.height
    );
  } else if (shape.type === "Circle") {
    // Determine if this is an ellipse or a circle
    const radiusX = shape.radiusX || shape.radius || 50;
    const radiusY = shape.radiusY || shape.radius || 50;
    const isEllipse = radiusX !== radiusY;

    if (isEllipse) {
      isInShape = isPointInEllipse(
        clickPoint.x,
        clickPoint.y,
        shape.x,
        shape.y,
        radiusX,
        radiusY
      );
    } else {
      isInShape = isPointInCircle(
        clickPoint.x,
        clickPoint.y,
        shape.x,
        shape.y,
        shape.radius || radiusX
      );
    }
  }

  if (!isInShape) {
    return {
      shouldSelectShape: false,
      shouldSelectImage: false,
      isOnStroke: false,
    };
  }

  // Check if click is in the fill area
  const isInFillArea = isPointInFillArea(clickPoint, shape);

  // Check for underlying images first
  const overlappingImages = images.filter((image) => {
    const isInImage = isPointInImage(clickPoint, image);
    return isInImage;
  });

  // Check shape transparency
  const shapeOpacity = getShapeOpacity(shape);


  // If there are no overlapping images, always select the shape
  if (overlappingImages.length === 0) {
    return {
      shouldSelectShape: true,
      shouldSelectImage: false,
      isOnStroke: false,
    };
  }

  // PRIORITY 1: If shape is transparent enough to see through (0-10% opacity) AND we're in the fill area, select the underlying image
  // (Note: Stroke clicks are already handled at PRIORITY 0 above)
  // Changed threshold from 0.8 to 0.1 to allow image selection when shape opacity is 0-10%
  if (shapeOpacity <= 0.1 && isInFillArea) {
    return {
      shouldSelectShape: false,
      shouldSelectImage: true,
      selectedImage: overlappingImages[overlappingImages.length - 1],
      isOnStroke: false,
    };
  }

  // PRIORITY 2: If shape is opaque or we're not in fill area, select the shape
  return {
    shouldSelectShape: true,
    shouldSelectImage: false,
    isOnStroke: false,
  };
};

/**
 * Simple hit detection wrapper function
 * @param {number} x - Point x coordinate
 * @param {number} y - Point y coordinate
 * @param {Object} element - Element to check
 * @param {string} elementType - Type of element
 * @returns {boolean} - True if point hits element
 */
export const checkSimpleHitDetection = (x, y, element, elementType) => {
  switch (elementType) {
    case "circle":
      return isPointInCircle(x, y, element.x, element.y, element.radius);
    case "rectangle":
      return isPointInRectangle(
        x,
        y,
        element.x,
        element.y,
        element.width,
        element.height
      );
    case "text":
      return isPointInText(
        x,
        y,
        element.x,
        element.y,
        element.width,
        element.height
      );
    default:
      return false;
  }
};
